import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CommonButton from '../../../../Buttons/CommonButton';
import Icon from '../../../../Icon/Icon';
import {
    SUPPORTED_URL_MACROS,
    buildDynamicUrlCreatives,
    isValidUrlTemplate,
} from '../dynamicUrlCreatives.js';

const TRANSLATIONS =
    'sections.campaigns.new-campaign.wizard-creatives.creatives.dynamic-url-modal';

const DynamicUrlModal = ({ isActive, screens = [], onClose, onConfirm }) => {
    const { t } = useTranslation();
    const [template, setTemplate] = useState('');
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const trimmedTemplate = template.trim();

    const validationError = useMemo(() => {
        if (!trimmedTemplate) return t(`${TRANSLATIONS}.errors.required`);
        if (!isValidUrlTemplate(trimmedTemplate)) {
            return t(`${TRANSLATIONS}.errors.invalid-url`);
        }
        return null;
    }, [trimmedTemplate, t]);

    const { perScreen, eligible, skipped, items } = useMemo(
        () =>
            validationError
                ? { perScreen: false, eligible: [], skipped: [], items: [] }
                : buildDynamicUrlCreatives(trimmedTemplate, screens),
        [validationError, trimmedTemplate, screens]
    );

    const close = () => {
        if (submitting) return;
        setTemplate('');
        setTouched(false);
        onClose();
    };

    useEffect(() => {
        if (!isActive) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    const confirm = async () => {
        setTouched(true);
        if (validationError || items.length === 0 || submitting) return;

        setSubmitting(true);
        try {
            const created = await onConfirm(items);
            if (created) {
                setTemplate('');
                setTouched(false);
                onClose();
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!isActive) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
            }}
            data-testid="DynamicUrlModal">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="dynamic-url-modal-title"
                className="flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white text-sm text-gray-700 shadow-2xl"
                style={{ maxHeight: '90vh' }}>
                <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
                    <div>
                        <h3
                            id="dynamic-url-modal-title"
                            className="text-lg font-medium text-gray-800">
                            {t(`${TRANSLATIONS}.title`)}
                        </h3>
                        <p className="mt-1 text-gray-500">
                            {t(`${TRANSLATIONS}.description`)}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        className="ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-black focus:outline-none"
                        aria-label={t('common.buttons.cancel')}>
                        <Icon name="times" fixedWidth />
                    </button>
                </div>

                <div className="flex flex-col space-y-4 overflow-y-auto px-6 py-4">
                    <div className="flex flex-col space-y-1">
                        <label
                            htmlFor="dynamic-url-modal-input"
                            className="font-medium text-gray-700">
                            {t(`${TRANSLATIONS}.url-label`)}
                        </label>
                        <input
                            id="dynamic-url-modal-input"
                            type="text"
                            value={template}
                            autoFocus
                            onChange={(event) => setTemplate(event.target.value)}
                            onBlur={() => setTouched(true)}
                            placeholder={t(`${TRANSLATIONS}.placeholder`)}
                            className={`w-full rounded-lg border px-3 py-2 focus:outline-none ${
                                touched && validationError
                                    ? 'border-red-400'
                                    : 'border-gray-300 focus:border-blue-400'
                            }`}
                            data-testid="DynamicUrlModal-input"
                        />
                        {touched && validationError && (
                            <p
                                className="text-xs text-red-600"
                                data-testid="DynamicUrlModal-error">
                                {validationError}
                            </p>
                        )}
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="font-medium text-gray-700">
                            {t(`${TRANSLATIONS}.macros-title`)}
                        </p>
                        <ul className="mt-2 space-y-1">
                            {SUPPORTED_URL_MACROS.map((macro) => (
                                <li
                                    key={macro.token}
                                    className="flex flex-wrap items-center gap-2">
                                    <code className="rounded bg-white px-2 py-0.5 font-mono text-xs text-blue-700 ring-1 ring-gray-200">
                                        {macro.token}
                                    </code>
                                    <span className="text-gray-600">
                                        {t(`${TRANSLATIONS}.macros.screen-id`, {
                                            example: macro.example,
                                        })}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-2 text-xs text-gray-500">
                            {t(`${TRANSLATIONS}.macros-help`)}
                        </p>
                    </div>

                    {!validationError && (
                        <div className="flex flex-col space-y-2">
                            {eligible.length === 0 ? (
                                <p className="rounded-lg bg-yellow-50 px-3 py-2 text-yellow-800">
                                    {t(`${TRANSLATIONS}.no-eligible-screens`)}
                                </p>
                            ) : perScreen ? (
                                <>
                                    <p className="font-medium">
                                        {t(`${TRANSLATIONS}.per-screen-summary`, {
                                            count: items.length,
                                        })}
                                    </p>
                                    <ul
                                        className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200"
                                        data-testid="DynamicUrlModal-preview">
                                        {items.map((item) => (
                                            <li
                                                key={item.externalId}
                                                className="flex flex-col px-3 py-1.5">
                                                <span className="text-xs font-medium text-gray-800">
                                                    {item.externalId}
                                                </span>
                                                <span className="break-all text-xs text-gray-500">
                                                    {item.url}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <p
                                    className="rounded-lg bg-blue-50 px-3 py-2 text-blue-800"
                                    data-testid="DynamicUrlModal-shared">
                                    {t(`${TRANSLATIONS}.shared-summary`, {
                                        count: eligible.length,
                                    })}
                                </p>
                            )}
                            {skipped.length > 0 && (
                                <p
                                    className="text-xs text-yellow-700"
                                    data-testid="DynamicUrlModal-skipped">
                                    {t(`${TRANSLATIONS}.skipped-screens`, {
                                        count: skipped.length,
                                    })}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end border-t border-gray-100 bg-gray-50 px-6 py-3">
                    <CommonButton
                        onClick={close}
                        label={t('common.buttons.cancel')}
                        styleType={'cancel'}
                        className="w-28"
                        disabled={submitting}
                    />
                    <CommonButton
                        onClick={confirm}
                        label={t(`${TRANSLATIONS}.confirm`)}
                        styleType={'primary'}
                        className="ml-2"
                        disabled={
                            submitting ||
                            Boolean(validationError) ||
                            items.length === 0
                        }
                        testId="DynamicUrlModal-confirm"
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DynamicUrlModal;
